import qs from 'query-string';

import { getFullUrlTo, getCurrentUrl } from '../../util/uri';
import { selectors as configSelectors } from '../config';

import {
    loginUser,
    logoutUser,
    verifyJWT,
    exchangePulseToken,
    authenticationProcessComplete,
    forceNewSession,
    loginUsingIdentity,
} from './actions';

import {
    LOGIN_FAILURE,
    LOGIN_SUCCESS,
    VERIFY_JWT_FAILURE,
    VERIFY_JWT_SUCCESS,
    EXCHANGE_PULSE_TOKEN_SUCCESS,
    LOGIN_USER_USING_SCHIBSTED_ACCOUNT,
    LOGIN_USER_USING_PULSE,
    LOGIN_DIFFERENT_ACCOUNT,
} from './types';

import {
    canRetry,
    cleanRetry,
    incrementRetry,
    getSchAccCode,
    getPulseCode,
    setJWT,
    removeJWT,
    getJWT,
    orInitializer,
    isQueryParamPresent,
    getSchAccState,
    decodeState,
    getAuthState,
} from './utils';

import {
    AUTH_SOURCE_TYPE_PULSE,
    AUTH_SOURCE_TYPE_SPID,
} from '../constants';

const LOGIN_INIT_ACTION = 'SCHIBSTED_ACCOUNT_HAS_SESSION_COMPLETE';
const LOGOUT_INIT_ACTION = 'SCHIBSTED_ACCOUNT_LOGOUT';
const SCHIBSTED_ACCOUNT_LOGIN_FAILURE = 'SCHIBSTED_ACCOUNT_HAS_SESSION_ERROR';

const dispatchAuthenticationProcessComplete = dispatch => dispatch(authenticationProcessComplete());

const verifyJwt = (dispatch, config, state) => {
    if (getJWT()) {
        const endpoint = `${config.apiHost}/login?spidUserId=${state.spid.userId}`;
        return dispatch(verifyJWT({ endpoint }));
    }
    return dispatch({
        type: VERIFY_JWT_FAILURE,
    });
};

const exchangeJWEToken = (dispatch, config) => {
    const endpoint = `${config.apiHost}/token/exchange/pulse`;
    dispatch(exchangePulseToken({ endpoint }));
};

const identifyUserUsingPulseCode = (dispatch, config, code) => {
    const queryPulseCode = getPulseCode();
    if (queryPulseCode) {
        dispatch(forceNewSession());
    }
    const endpoint = `${config.apiHost}/login?${qs.stringify({
        pulseCode: queryPulseCode || code,
        publication: config.publication,
    })}`;
    dispatch(loginUser({ endpoint }));
};

export const loginUserUsingSchibstedAccount = (dispatch, config, state) => {
    if (!canRetry()) {
        return;
    }

    if (!getSchAccCode()) {
        dispatch(loginUsingIdentity(state?.spid));
        return;
    }

    const schAccState = getSchAccState();
    const authState = getAuthState();

    const schAccStateData = decodeState(schAccState);
    const authStateData = decodeState(authState);

    if (!schAccState) {
        dispatch(loginUsingIdentity({ redirectLocation: { pathname: CONFIG.basePath } }));
        return;
    }

    // State from an external source (e.g. Sourcepoint consent manager login) that we cannot decode — skip nonce validation and proceed directly to code exchange.
    // The OAuth code is single-use and bound to the redirect_uri, so this is safe.
    const isExternalLoginState = schAccState && !schAccStateData;

    // loginNonce from state in localStorage doesn't match with one from query param
    if (!isExternalLoginState && (!authState || authStateData?.loginNonce !== schAccStateData?.loginNonce)) {
        const redirectLocation = schAccStateData?.redirectLocation;
        const pathname = redirectLocation?.pathname ?? CONFIG.basePath;
        const search = redirectLocation?.search ?? '';

        dispatch(loginUsingIdentity({ redirectLocation: { pathname, search } }));
        return;
    }

    const endpoint = `${config.apiHost}/login?${qs.stringify({
        redirect_uri: getFullUrlTo('/login'),
        code: getSchAccCode(),
        publication: config.publication,
    })}`;
    dispatch(loginUser({ endpoint }));
};

const isReturningFromSpidPaymentFlow = () => isQueryParamPresent('payment_id')
    && !isQueryParamPresent('subscription_id');

const initializeAuthFlow = (dispatch, config, state) => {
    const isPulseAllowed = config.allowedAuthSources.includes(AUTH_SOURCE_TYPE_PULSE);

    orInitializer(dispatch, config, state)([
        [isPulseAllowed && getPulseCode(), identifyUserUsingPulseCode],
        [getSchAccCode() && !isReturningFromSpidPaymentFlow(), loginUserUsingSchibstedAccount],
        [true, verifyJwt],
    ]);
};

const authenticateUser = (dispatch, config, state) => {
    const { loggedIn: loggedInWithSPiD } = state.spid;
    const isPulseAllowed = config.allowedAuthSources.includes(AUTH_SOURCE_TYPE_PULSE);

    orInitializer(dispatch, config, state)([
        [isPulseAllowed && !loggedInWithSPiD && !!getPulseCode(), identifyUserUsingPulseCode],
        [isPulseAllowed && !loggedInWithSPiD, exchangeJWEToken],
        [true, loginUserUsingSchibstedAccount],
    ]);
};

const validateAuthentication = (dispatch, config, state, payload) => {
    const { source } = payload.data;
    const { loggedIn: loggedInWithSPiD } = state.spid;
    const isPulseAllowed = config.allowedAuthSources.includes(AUTH_SOURCE_TYPE_PULSE);
    const equalSource = authSource => authSource === source;
    const shouldLoginPulse =
        (getPulseCode() || !loggedInWithSPiD) && equalSource(AUTH_SOURCE_TYPE_SPID);
    const shouldLoginSPiD =
        (loggedInWithSPiD || !isPulseAllowed) && equalSource(AUTH_SOURCE_TYPE_PULSE);

    orInitializer(dispatch, config, state)([
        [isPulseAllowed && shouldLoginPulse, exchangeJWEToken],
        [shouldLoginSPiD, loginUserUsingSchibstedAccount],
        [true, dispatchAuthenticationProcessComplete],
    ]);
};

const setLoggedInTokenAndClean = (dispatch, payload) => {
    setJWT(payload?.data?.jwt);
    cleanRetry();

    dispatchAuthenticationProcessComplete(dispatch);
};

const retryLoginProcess = (dispatch, config, state) => {
    const { loggedIn: loggedInWithSPiD } = state.spid;

    if (getJWT()) {
        removeJWT();
    }

    const forceSchibstedAccount = !state.config.allowedAuthSources.includes(AUTH_SOURCE_TYPE_PULSE);

    if (canRetry()) {
        incrementRetry();
        if (loggedInWithSPiD || forceSchibstedAccount) {
            dispatch(loginUsingIdentity(state?.spid));
        } else {
            authenticateUser(dispatch, config, state);
        }
    } else {
        authenticateUser(dispatch, config, state);
    }
};

const logoutUserFromMyPage = (dispatch, config, login) => {
    if (!login.forcedSession) {
        dispatch(logoutUser({ endpoint: `${config.apiHost}/logout` }));
        removeJWT();
    }
};

export const enhanceActionWithRedirectUrl = (config, state, action) => {
    if (!action?.payload?.redirectUri) {
        const shouldRedirectAfterLogout = configSelectors.shouldRedirectAfterLogout(state);
        const redirectAfterLogoutUri =
            config.allowedAuthSources.includes(AUTH_SOURCE_TYPE_PULSE) && !shouldRedirectAfterLogout
                ? getCurrentUrl({ pathname: window.location.pathname })
                : CONFIG.myPage.afterLogoutRedirectUri || CONFIG.myPage.frontPageUri;

        // eslint-disable-next-line no-param-reassign
        action.payload = {
            ...action.payload,
            redirectUri: redirectAfterLogoutUri,
        };
    }
};

export default ({ dispatch, getState }) => next => (action) => {
    const { type, payload } = action;
    const { config, login } = getState();

    if (!config.authenticateUser) {
        return next(action);
    }

    switch (type) {
        case LOGIN_INIT_ACTION:
            initializeAuthFlow(dispatch, config, getState());
            return next(action);
        case VERIFY_JWT_FAILURE:
            removeJWT();
            authenticateUser(dispatch, config, getState());
            return next(action);
        case VERIFY_JWT_SUCCESS:
            validateAuthentication(dispatch, config, getState(), payload);
            return next(action);
        case EXCHANGE_PULSE_TOKEN_SUCCESS:
            identifyUserUsingPulseCode(dispatch, config, payload?.data?.code);
            return next(action);
        case LOGIN_SUCCESS:
            setLoggedInTokenAndClean(dispatch, payload);
            return next(action);
        case LOGIN_FAILURE:
            retryLoginProcess(dispatch, config, getState());
            return next(action);
        case SCHIBSTED_ACCOUNT_LOGIN_FAILURE:
            retryLoginProcess(dispatch, config, getState());
            return next(action);
        case LOGIN_USER_USING_SCHIBSTED_ACCOUNT:
            dispatch(loginUsingIdentity(payload));
            return next(action);
        case LOGIN_USER_USING_PULSE:
            authenticateUser(dispatch, config, getState());
            return next(action);
        case LOGOUT_INIT_ACTION:
            logoutUserFromMyPage(dispatch, config, login);
            enhanceActionWithRedirectUrl(config, getState(), action);
            return next(action);
        case LOGIN_DIFFERENT_ACCOUNT:
            removeJWT();
            dispatch(loginUsingIdentity(payload));
            return next(action);
        default:
            return next(action);
    }
};
